import { SelectedChart } from "../components/Widgets/WidgetTypes";
import { Field, Module } from "../types/TenantInterfaces";
import { Filter } from "../types/GlobalInterfaces";

/**
 * Function which returns the group field information
 * (Supports SingleReference Related Fields).
 * @param {*} groupByField The field use to group the data.
 * @param {*} moduleAPIName Optional. Only provide it for Related Fields ("SingleReference" Fields).
 * @returns
 */
export function GetGroupByFieldAndType({
  groupByField,
  fieldMap,
  getModule,
  moduleAPIName,
}: {
  groupByField: string;
  fieldMap: { [fieldAPIName: string]: Field };
  moduleAPIName?: string;
  getModule: (modAPIName: string) => Module | null;
}) {
  const splitField = groupByField?.split(".") ?? [];
  const updatedSplitArray = splitField.slice(1);
  let field = null;

  if (moduleAPIName) {
    const module = getModule(moduleAPIName);
    if (!module)
      return { groupByField: "", groupByFieldType: "", groupByFieldName: "" };
    field = module?.Fields.find((f) => f.APIName === splitField[0]);
  } else {
    field = fieldMap[splitField[0]];
  }

  if (!field)
    return { groupByField: "", groupByFieldType: "", groupByFieldName: "" };

  switch (field?.FieldType) {
    // Related Field Support
    case "SingleReference":
      // This means it's a related field.
      if (updatedSplitArray?.length) {
        const referencedModule =
          typeof field?.SourcesList === "string"
            ? field?.SourcesList
            : field?.SourcesList?.[0];

        const moduleAPIName = referencedModule?.replace("Tenant.Modules.", "");
        return GetGroupByFieldAndType({
          groupByField: updatedSplitArray.join("."),
          moduleAPIName,
          fieldMap,
          getModule,
        });
      } else {
        return {
          groupByField: field?.APIName,
          groupByFieldType: field?.FieldType,
          groupByFieldName: field?.Name,
        };
      }
    case "Tags":
      return {
        groupByField: field?.APIName,
        groupByFieldType: field?.FieldType,
        groupByFieldName: field?.Name,
      };
    case "MultiSubitem":
      // TODO: Add support for MultiSubitem. Check the API too.
      return { groupByField: "", groupByFieldType: "", groupByFieldName: "" };
    case "Subitem":
      // TODO: Add support for Subitem. Check the API too.
      return { groupByField: "", groupByFieldType: "", groupByFieldName: "" };
    default:
      return {
        groupByField: field?.APIName,
        groupByFieldType: field?.FieldType,
        groupByFieldName: field?.Name,
      };
  }
}

/**
 * For Single GroupBy only.
 *
 * Function which modifies the query to filter the record based on the
 * selected group.
 */
export function OnGroupClick({
  groupByField,
  groupByFieldType,
  entry,
  rawGroupByField,
  onSelectGroup,
  filters,
}: {
  /** Group by field APIName. */
  groupByField: string;
  /** Group by field type  */
  groupByFieldType: string;
  /** The group data */
  entry: any;
  /** Used for Location field types. Contains the full path of the field used to group the data. */
  rawGroupByField: string;
  /** Callback function which modifies the query based on the selected group. */
  onSelectGroup?: (chartGroup: SelectedChart) => void;
  /** The filter applied to the widget. */
  filters?: Filter[];
}) {
  let value = entry?.[groupByField];

  // Transforming the value base on the Field Type.
  if (groupByFieldType === "SingleReference") {
    value = entry?.OriginalValue?.id ?? entry?.[groupByField];
  } else if (groupByFieldType === "MultiReference") {
    value =
      (entry?.OriginalValue?.[0]?.id || entry?.OriginalValue?.id) ??
      entry?.[groupByField];

    if (value === null || value === undefined || value === "") {
      value = undefined;
    }
  }

  /**
   * Property which contains the parent record of the selected group.
   *
   * Only accessible for `Subitem`, `MultiSubitem` and  Related `SingleReference` fields.
   * Note: Subitem and MultiSubitem fields are not supported yet for widgets. Date: January 3, 2025.
   */
  // Commented this because this was causing issues with the Filters
  // if (entry?.ParentRecord) {
  //   const keys = Object.keys(entry?.ParentRecord);
  //   groupByField = keys[0]; // It's the parent field APIName
  //   value = entry?.ParentRecord?.[groupByField]?.id;
  // }

  const useRawField = rawGroupByField.includes(".");
  let groupFieldAPIName = useRawField ? rawGroupByField : groupByField;

  if (value === "" || value === undefined || value === null) {
    value = undefined;
  }

  onSelectGroup &&
    onSelectGroup({
      group: [
        {
          Group: false,
          groupByField: groupFieldAPIName,
          groupByValue: value,
        },
      ],
      filters,
    });
}

/**
 * Function which checks if the group is selected or not.
 */
export function IsGroupSelected({
  selectedGroup,
  groupValue,
  groupByField,
  rawGroupByField,
  groupByFieldType,
}: {
  selectedGroup: SelectedChart | undefined;
  groupValue: any;
  groupByField: string;
  rawGroupByField: string;
  groupByFieldType: string;
}) {
  if (!selectedGroup || !selectedGroup?.group?.length || !groupValue)
    return false;

  const group = selectedGroup.group;
  let entryValue = groupValue?.[groupByField];

  if (groupByFieldType === "SingleReference") {
    entryValue = groupValue?.OriginalValue?.id ?? groupValue?.[groupByField];
  } else if (groupByFieldType === "MultiReference") {
    entryValue =
      (groupValue?.OriginalValue?.[0]?.id || groupValue?.OriginalValue?.id) ??
      groupValue?.[groupByField];
  } else if (groupByFieldType === "Location") {
    const groupByFieldAPIName = groupByField.split(".")[0];
    entryValue = groupValue?.[groupByFieldAPIName];
  }

  if (entryValue === "" || entryValue === undefined || entryValue === null) {
    entryValue = undefined;
  }

  const useRawField = rawGroupByField.includes(".");
  let groupField = useRawField ? rawGroupByField : groupByField;

  return !!group.find((g: any) => {
    if (!!g?.Group) {
      return g.Groups.find(
        (sg: any) =>
          sg.groupByField === groupField && entryValue === sg.groupByValue
      );
    }

    return groupField === g?.groupByField && entryValue === g?.groupByValue;
  });
}

/**
 * For Bar and FilledLine charts.
 */
export function TickFormatter({
  value,
  groupByFieldName,
  groupByField,
  groupByFieldType,
  chartData,
}: {
  value: any;
  chartData: any[];
  groupByFieldName: string;
  groupByFieldType: string;
  groupByField: string;
}) {
  if (groupByFieldType === "Tags") {
    value = chartData.find((d) => d[groupByField] === value)?.Name;
  }

  return value || `No ${groupByFieldName}`;
}
